import { ClientFormValues, ContactFormValues } from '../components/clients-form'
import dayjs, { Dayjs } from 'dayjs'

export interface CuratorFormValue {
    user?: string
    percent?: number
}

export interface NormalizedContactInput {
    documentId?: string
    data: {
        address?: string
        pointName?: string
        receiverName?: string
        phone?: string
        workingHours?: string
        latCoordinates?: number
        longCoordinates?: number
        comment?: string
        isPrimary?: boolean | null
    }
    hasData: boolean
}

export interface NormalizedCuratorInput {
    data: {
        user?: { connect: string[] }
        percent?: number
    }
    hasData: boolean
}

export interface NormalizedClientFormData {
    base: {
        name: string
        approvalStatus?: ClientFormValues['approvalStatus']
        legalAddress?: string
        defermentInDays?: number
        source?: ClientFormValues['source']
        exhibitionName?: string
        comment?: string
    }
    curators: NormalizedCuratorInput[]
    contacts: NormalizedContactInput[]
}

const formatWorkingHours = (value: [Dayjs, Dayjs] | null | undefined): string | undefined => {
    if (!value || !Array.isArray(value) || value.length !== 2) return undefined
    const [start, end] = value
    if (!start || !end || !dayjs.isDayjs(start) || !dayjs.isDayjs(end)) return undefined
    return `${start.format('HH:mm')}-${end.format('HH:mm')}`
}

export const hasContactData = (contact: ContactFormValues): boolean =>
    Boolean(
        (!contact?.noAddress && contact?.address) ||
            contact?.pointName ||
            contact?.receiverName ||
            contact?.phone ||
            contact?.workingHours ||
            contact?.latCoordinates !== undefined ||
            contact?.longCoordinates !== undefined ||
            contact?.comment ||
            contact?.isPrimary !== undefined
    )

export const normalizeClientFormValues = (values: ClientFormValues): NormalizedClientFormData => {
    const contacts = (values.contacts || []).map<NormalizedContactInput>(contact => {
        let workingHours: string | undefined
        if (Array.isArray(contact.workingHours) && contact.workingHours.length === 2) {
            workingHours = formatWorkingHours(contact.workingHours as [Dayjs, Dayjs])
        } else if (typeof contact.workingHours === 'string') {
            workingHours = contact.workingHours
        }

        const noAddress = Boolean(contact.noAddress)

        return {
            documentId: contact.documentId,
            data: {
                address: noAddress ? '' : contact.address,
                pointName: contact.pointName,
                receiverName: contact.receiverName,
                phone: contact.phone,
                workingHours,
                latCoordinates: noAddress ? undefined : contact.latCoordinates,
                longCoordinates: noAddress ? undefined : contact.longCoordinates,
                comment: contact.comment,
                isPrimary: contact.isPrimary ?? null,
            },
            hasData: hasContactData(contact),
        }
    })

    const curators = (values.curators || []).map<NormalizedCuratorInput>(curator => ({
        data: {
            user: curator.user ? { connect: [curator.user] } : undefined,
            percent: curator.percent,
        },
        hasData: Boolean(curator.user && curator.percent !== undefined && curator.percent !== null),
    }))

    return {
        base: {
            name: values.name,
            approvalStatus: values.approvalStatus,
            legalAddress: values.noLegalAddress ? '' : values.legalAddress,
            defermentInDays: values.defermentInDays,
            source: values.source,
            exhibitionName: values.exhibitionName,
            comment: values.comment,
        },
        curators,
        contacts,
    }
}
